import { useCallback, useEffect, useMemo, useState } from 'react';
import { WEBHOOK_EVENT_GROUPS, WEBHOOK_EVENT_LABEL, type WebhookEvent } from '@machs/shared';
import { api } from '../lib/api';
import { shortDate } from '../lib/format';
import { Icon } from '../lib/icons';
import { usePlan } from '../lib/plan';
import { useSync } from '../lib/sync';
import { Dialog, useToast } from './ui';

// Andere Systeme anbinden: API-Schlüssel für den MCP-Server und ausgehende
// Webhooks (server/src/routes/integrations.ts). Nur für Inhaber und Admins.

interface ApiKey {
  id: string;
  name: string;
  prefix: string;
  canWrite: boolean;
  createdAt: string;
  createdBy: { id: string; name: string } | null;
  lastUsedAt: string | null;
}

interface Webhook {
  id: string;
  name: string;
  url: string;
  events: WebhookEvent[];
  projectIds: string[] | null;
  active: boolean;
  disabledReason: string | null;
  createdAt: string;
  lastDelivery: { status: 'pending' | 'ok' | 'failed'; at: string; responseStatus: number | null } | null;
  failedRecently: number;
}

interface Delivery {
  id: string;
  event: string;
  status: 'pending' | 'ok' | 'failed';
  attempts: number;
  responseStatus: number | null;
  error: string | null;
  createdAt: string;
  deliveredAt: string | null;
  nextAttemptAt: string | null;
  payload: unknown;
}

const time = (iso: string) => `${shortDate(iso)}, ${new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr`;

function copy(text: string, toast: (m: string) => void, what = 'Kopiert') {
  void navigator.clipboard?.writeText(text).then(() => toast(what));
}

export function IntegrationsSection({ mcpUrl }: { mcpUrl: string | null }) {
  const plan = usePlan();
  const toast = useToast();
  const [keys, setKeys] = useState<ApiKey[] | null>(null);
  const [hooks, setHooks] = useState<Webhook[] | null>(null);
  const [newKey, setNewKey] = useState<{ key: string; info: ApiKey } | null>(null);
  const [keyName, setKeyName] = useState('');
  const [keyWrite, setKeyWrite] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<Webhook | 'neu' | null>(null);
  const [log, setLog] = useState<Webhook | null>(null);
  const [removing, setRemoving] = useState<{ kind: 'key'; item: ApiKey } | { kind: 'hook'; item: Webhook } | null>(null);
  const [secret, setSecret] = useState<{ hook: string; value: string; fresh: boolean } | null>(null);

  const load = useCallback(async () => {
    try {
      const [k, h] = await Promise.all([api<{ keys: ApiKey[] }>('/integrations/api-keys'), api<{ webhooks: Webhook[] }>('/integrations/webhooks')]);
      setKeys(k.keys);
      setHooks(h.webhooks);
    } catch {
      setKeys([]);
      setHooks([]);
    }
  }, []);
  useEffect(() => void load(), [load]);

  async function createKey(e: React.FormEvent) {
    e.preventDefault();
    if (!keyName.trim() || !plan.allowFeature(keyWrite ? 'mcpWrite' : 'mcp')) return;
    setBusy(true);
    try {
      setNewKey(await api<{ key: string; info: ApiKey }>('/integrations/api-keys', { method: 'POST', json: { name: keyName.trim(), canWrite: keyWrite } }));
      setKeyName('');
      setKeyWrite(false);
      void load();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Schlüssel konnte nicht angelegt werden');
    } finally {
      setBusy(false);
    }
  }

  async function toggleHook(h: Webhook) {
    if (!h.active && !plan.allowFeature('webhooks')) return;
    try {
      await api(`/integrations/webhooks/${h.id}`, { method: 'PATCH', json: { active: !h.active } });
      void load();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Nicht gespeichert');
    }
  }

  async function test(h: Webhook) {
    if (!plan.allowFeature('webhooks')) return;
    try {
      const { delivery } = await api<{ delivery: Delivery }>(`/integrations/webhooks/${h.id}/test`, { method: 'POST' });
      toast(delivery.status === 'ok' ? `Testsendung angekommen (HTTP ${delivery.responseStatus})` : `Testsendung gescheitert: ${delivery.error ?? 'unbekannter Fehler'}`);
      void load();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Test fehlgeschlagen');
    }
  }

  async function showSecret(h: Webhook, rotate = false) {
    try {
      const r = await api<{ secret: string }>(`/integrations/webhooks/${h.id}/secret`, rotate ? { method: 'POST' } : {});
      setSecret({ hook: h.name, value: r.secret, fresh: rotate });
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Nicht verfügbar');
    }
  }

  async function confirmRemove() {
    if (!removing) return;
    try {
      await api(removing.kind === 'key' ? `/integrations/api-keys/${removing.item.id}` : `/integrations/webhooks/${removing.item.id}`, { method: 'DELETE' });
      toast(removing.kind === 'key' ? 'Schlüssel widerrufen' : 'Webhook gelöscht');
      setRemoving(null);
      void load();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Nicht gelöscht');
    }
  }

  const noMcp = plan.info && !plan.info.features.includes('mcp');
  const noHooks = plan.info && !plan.info.features.includes('webhooks');

  return (
    <section className="flex flex-col gap-3 border-t border-[var(--line)] pt-4">
      <div>
        <h3 className="m-0 mb-1 text-[14px] font-semibold">Andere Systeme anbinden</h3>
        <p className="m-0 text-[13.5px] text-[var(--muted)]">
          Für Automatisierungen (n8n, Make, Zapier), eigene Server oder Claude Code: Ein API-Schlüssel öffnet dieselbe MCP-Schnittstelle wie oben, ganz ohne Anmeldung im Browser. Webhooks melden neue Aufgaben, Nachrichten und Rückfragen sofort an eine Adresse eurer Wahl.
        </p>
      </div>

      {/* ---------- API-Schlüssel ---------- */}
      <h4 className="m-0 mt-1 flex items-center gap-2 text-[13.5px] font-semibold">
        API-Schlüssel{noMcp && <span className="plus-tag">Plus</span>}
      </h4>
      {newKey && (
        <div className="check ok flex-col items-stretch gap-2">
          <span>
            Schlüssel „{newKey.info.name}“ angelegt. <b>Er wird nur jetzt angezeigt</b> – bitte gleich im anderen System hinterlegen.
          </span>
          <div className="flex gap-2">
            <input className="input h-10 min-w-0 flex-1 font-mono text-[12.5px]" readOnly value={newKey.key} aria-label="Neuer API-Schlüssel" onFocus={(e) => e.currentTarget.select()} />
            <button className="btn btn-ghost h-10" onClick={() => copy(newKey.key, toast, 'Schlüssel kopiert')}>Kopieren</button>
          </div>
          <small className="text-[12.5px] text-[var(--muted)]">
            Verbindung: MCP-Server <code>{mcpUrl ?? '/mcp'}</code> mit dem Header <code>Authorization: Bearer {newKey.key.slice(0, 14)}…</code>
          </small>
          <button className="chip self-start" onClick={() => setNewKey(null)}>Erledigt</button>
        </div>
      )}
      {keys && keys.length > 0 && (
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {keys.map((k) => (
            <li key={k.id} className="flex items-center gap-2 rounded-2xl border border-[var(--line)] bg-[var(--surface)] px-3 py-2">
              <Icon name="key" size={18} className="text-[var(--brand-a)]" />
              <span className="min-w-0 flex-1">
                <b className="block truncate text-[14px]">
                  {k.name} <span className="font-mono text-[12px] font-normal text-[var(--muted)]">{k.prefix}</span>
                </b>
                <small className="text-xs text-[var(--muted)]">
                  {k.canWrite ? 'Lesen und schreiben' : 'Nur lesen'} · angelegt {shortDate(k.createdAt)}
                  {k.createdBy ? ` von ${k.createdBy.name}` : ''}
                  {k.lastUsedAt ? ` · zuletzt genutzt ${shortDate(k.lastUsedAt)}` : ' · noch nicht genutzt'}
                </small>
              </span>
              <button className="chip" onClick={() => setRemoving({ kind: 'key', item: k })}>Widerrufen</button>
            </li>
          ))}
        </ul>
      )}
      <form className="flex flex-wrap items-center gap-2" onSubmit={(e) => void createKey(e)}>
        <input className="input h-10 min-w-[180px] flex-1" placeholder="Name, z. B. n8n oder Buchhaltung" value={keyName} onChange={(e) => setKeyName(e.target.value)} aria-label="Name des Schlüssels" maxLength={80} />
        <label className="flex items-center gap-2 text-[13.5px]" data-tip="Aufgaben anlegen, Nachrichten senden, Status setzen" data-tip-wrap="">
          <button type="button" className={`switch ${keyWrite ? 'on' : ''}`} role="switch" aria-checked={keyWrite} aria-label="Darf schreiben" onClick={() => setKeyWrite((v) => !v)} />
          Darf schreiben
          {plan.info && !plan.info.features.includes('mcpWrite') && <span className="plus-tag">Business</span>}
        </label>
        <button className="btn btn-primary h-10" disabled={!keyName.trim() || busy}>
          <Icon name="plus" size={17} />Schlüssel anlegen
        </button>
      </form>
      <p className="m-0 text-[12.5px] text-[var(--muted)]">
        Ein Schlüssel handelt im Namen der Person, die ihn anlegt; Nachrichten erscheinen als „Name via Schlüssel …“. Verlässt sie das Team, gilt er nicht mehr.
      </p>

      {/* ---------- Webhooks ---------- */}
      <h4 className="m-0 mt-3 flex items-center gap-2 text-[13.5px] font-semibold">
        Webhooks{noHooks && <span className="plus-tag">Business</span>}
      </h4>
      {hooks && hooks.length > 0 && (
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {hooks.map((h) => (
            <li key={h.id} className="rounded-2xl border border-[var(--line)] bg-[var(--surface)] px-3 py-2.5">
              <div className="flex items-center gap-2">
                <Icon name="send" size={18} className={h.active ? 'text-[var(--brand-a)]' : 'text-[var(--dim)]'} />
                <span className="min-w-0 flex-1">
                  <b className="block truncate text-[14px]">{h.name}</b>
                  <small className="block truncate font-mono text-[12px] text-[var(--muted)]">{h.url}</small>
                </span>
                <button className={`switch ${h.active ? 'on' : ''}`} role="switch" aria-checked={h.active} aria-label={`${h.name} aktiv`} onClick={() => void toggleHook(h)} />
              </div>
              <small className="mt-1 block text-[12.5px] text-[var(--muted)]">
                {h.events.length} {h.events.length === 1 ? 'Ereignis' : 'Ereignisse'}
                {h.projectIds ? ` · ${h.projectIds.length} ${h.projectIds.length === 1 ? 'Projekt' : 'Projekte'}` : ' · alle Projekte'}
                {h.lastDelivery &&
                  ` · zuletzt ${h.lastDelivery.status === 'ok' ? 'zugestellt' : h.lastDelivery.status === 'pending' ? 'ausstehend' : 'gescheitert'} ${time(h.lastDelivery.at)}`}
                {h.failedRecently > 0 && ` · ${h.failedRecently} gescheitert in 24 h`}
              </small>
              {h.disabledReason && <div className="check mt-2">{h.disabledReason}</div>}
              <div className="mt-2 flex flex-wrap gap-1.5">
                <button className="chip" onClick={() => plan.allowFeature('webhooks') && setEditing(h)}>Bearbeiten</button>
                <button className="chip" onClick={() => void test(h)}>Testen</button>
                <button className="chip" onClick={() => setLog(h)}>Protokoll</button>
                <button className="chip" onClick={() => void showSecret(h)}>Signatur-Geheimnis</button>
                <button className="chip" onClick={() => setRemoving({ kind: 'hook', item: h })}>Löschen</button>
              </div>
            </li>
          ))}
        </ul>
      )}
      <button className="btn btn-ghost self-start" onClick={() => plan.allowFeature('webhooks') && setEditing('neu')}>
        <Icon name="plus" size={18} />Webhook hinzufügen
      </button>
      <p className="m-0 text-[12.5px] text-[var(--muted)]">
        Jede Sendung ist ein POST mit JSON und der Signatur im Header <code>X-Machs-Signature</code> (HMAC-SHA256 über Zeitstempel und Inhalt). Antwortet die Adresse nicht mit 2xx, versucht Mach's es bis zu sechsmal erneut, zuletzt nach zwölf Stunden.
      </p>

      {editing && (
        <WebhookDialog
          hook={editing === 'neu' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(created) => {
            setEditing(null);
            if (created) setSecret({ hook: created.name, value: created.secret, fresh: true });
            void load();
          }}
        />
      )}
      {log && <DeliveryLog hook={log} onClose={() => (setLog(null), void load())} />}
      {secret && (
        <Dialog title="Signatur-Geheimnis" onClose={() => setSecret(null)} footer={<button className="btn btn-primary" onClick={() => setSecret(null)}>Fertig</button>}>
          <p className="m-0 mb-3 text-[14px]">
            {secret.fresh ? `Neues Geheimnis für „${secret.hook}“.` : `Geheimnis für „${secret.hook}“.`} Damit prüft das empfangende System, dass eine Sendung wirklich von Mach's kommt.
          </p>
          <div className="flex gap-2">
            <input className="input h-10 min-w-0 flex-1 font-mono text-[12.5px]" readOnly value={secret.value} aria-label="Signatur-Geheimnis" onFocus={(e) => e.currentTarget.select()} />
            <button className="btn btn-ghost h-10" onClick={() => copy(secret.value, toast, 'Geheimnis kopiert')}>Kopieren</button>
          </div>
          <p className="m-0 mt-3 text-[12.5px] text-[var(--muted)]">
            Prüfen: Aus <code>X-Machs-Signature: t=…,v1=…</code> den Zeitstempel <code>t</code> nehmen, HMAC-SHA256 mit diesem Geheimnis über <code>t + "." + Rohinhalt</code> bilden und hexadezimal mit <code>v1</code> vergleichen. Ältere Zeitstempel als fünf Minuten verwerfen.
          </p>
          {!secret.fresh && (
            <button
              className="chip mt-3"
              onClick={() => {
                const hook = hooks?.find((h) => h.name === secret.hook);
                if (hook) void showSecret(hook, true);
              }}
            >
              Neues Geheimnis erzeugen
            </button>
          )}
        </Dialog>
      )}
      {removing && (
        <Dialog
          title={removing.kind === 'key' ? 'Schlüssel widerrufen?' : 'Webhook löschen?'}
          onClose={() => setRemoving(null)}
          footer={
            <>
              <button className="btn btn-ghost" onClick={() => setRemoving(null)}>Abbrechen</button>
              <button className="btn btn-danger" onClick={() => void confirmRemove()}>{removing.kind === 'key' ? 'Widerrufen' : 'Löschen'}</button>
            </>
          }
        >
          <p className="m-0 text-[14px]">
            {removing.kind === 'key'
              ? `„${removing.item.name}“ hat danach sofort keinen Zugang mehr. Das lässt sich nicht rückgängig machen.`
              : `„${removing.item.name}“ bekommt danach keine Ereignisse mehr; das Protokoll wird mit gelöscht.`}
          </p>
        </Dialog>
      )}
    </section>
  );
}

function WebhookDialog({ hook, onClose, onSaved }: { hook: Webhook | null; onClose: () => void; onSaved: (created?: { name: string; secret: string }) => void }) {
  const { client, version } = useSync();
  const toast = useToast();
  const [name, setName] = useState(hook?.name ?? '');
  const [url, setUrl] = useState(hook?.url ?? '');
  const [events, setEvents] = useState<WebhookEvent[]>(hook?.events ?? ['task.created', 'task.completed', 'message.created', 'question.asked']);
  const [projectIds, setProjectIds] = useState<string[] | null>(hook?.projectIds ?? null);
  const [busy, setBusy] = useState(false);
  const projects = useMemo(
    () => [...client.data.project.values()].filter((p) => !p.archivedAt).sort((a, b) => a.name.localeCompare(b.name, 'de')),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [client, version],
  );

  const toggle = (e: WebhookEvent) => setEvents((list) => (list.includes(e) ? list.filter((x) => x !== e) : [...list, e]));
  const toggleProject = (id: string) =>
    setProjectIds((list) => {
      const next = (list ?? []).includes(id) ? (list ?? []).filter((x) => x !== id) : [...(list ?? []), id];
      return next.length ? next : null;
    });

  async function save() {
    setBusy(true);
    try {
      const body = { name: name.trim(), url: url.trim(), events, projectIds };
      if (hook) {
        await api(`/integrations/webhooks/${hook.id}`, { method: 'PATCH', json: body });
        toast('Webhook gespeichert');
        onSaved();
      } else {
        const r = await api<{ webhook: Webhook; secret: string }>('/integrations/webhooks', { method: 'POST', json: body });
        onSaved({ name: r.webhook.name, secret: r.secret });
      }
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Nicht gespeichert');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      title={hook ? 'Webhook bearbeiten' : 'Webhook hinzufügen'}
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>Abbrechen</button>
          <button className="btn btn-primary" disabled={busy || !url.trim() || events.length === 0} onClick={() => void save()}>
            {hook ? 'Speichern' : 'Hinzufügen'}
          </button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <label className="field">
          <span>Adresse</span>
          <input className="input" type="url" placeholder="https://example.com/machs-webhook" value={url} onChange={(e) => setUrl(e.target.value)} />
        </label>
        <label className="field">
          <span>Name (optional)</span>
          <input className="input" placeholder="z. B. n8n oder Ticketsystem" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} />
        </label>
        {WEBHOOK_EVENT_GROUPS.map((g) => (
          <div key={g.label}>
            <b className="mb-1.5 block text-[13px]">{g.label}</b>
            <div className="flex flex-wrap gap-1.5">
              {g.events.map((e) => (
                <button key={e} type="button" className="chip" aria-pressed={events.includes(e)} onClick={() => toggle(e)} data-tip={e} data-tip-below="">
                  {events.includes(e) && <Icon name="check" size={13} />}
                  {WEBHOOK_EVENT_LABEL[e]}
                </button>
              ))}
            </div>
          </div>
        ))}
        <div>
          <b className="mb-1.5 block text-[13px]">Projekte</b>
          <div className="flex flex-wrap gap-1.5">
            <button type="button" className="chip" aria-pressed={projectIds === null} onClick={() => setProjectIds(null)}>
              {projectIds === null && <Icon name="check" size={13} />}Alle Projekte
            </button>
            {projects.map((p) => (
              <button key={p.id} type="button" className="chip" aria-pressed={!!projectIds?.includes(p.id)} onClick={() => toggleProject(p.id)}>
                {projectIds?.includes(p.id) && <Icon name="check" size={13} />}
                {p.name}
              </button>
            ))}
          </div>
        </div>
      </div>
    </Dialog>
  );
}

function DeliveryLog({ hook, onClose }: { hook: Webhook; onClose: () => void }) {
  const toast = useToast();
  const [list, setList] = useState<Delivery[] | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const load = useCallback(
    () => void api<{ deliveries: Delivery[] }>(`/integrations/webhooks/${hook.id}/deliveries`).then((r) => setList(r.deliveries), () => setList([])),
    [hook.id],
  );
  useEffect(() => load(), [load]);

  async function retry(d: Delivery) {
    try {
      await api(`/integrations/webhooks/${hook.id}/deliveries/${d.id}/retry`, { method: 'POST' });
      toast('Wird erneut gesendet');
      setTimeout(load, 1500);
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Nicht möglich');
    }
  }

  return (
    <Dialog title={`Protokoll: ${hook.name}`} onClose={onClose} wide>
      {!list ? (
        <div className="grid place-items-center py-6"><div className="spinner" /></div>
      ) : list.length === 0 ? (
        <p className="m-0 text-[14px] text-[var(--muted)]">Noch keine Sendungen. Mit „Testen“ lässt sich eine Probe schicken.</p>
      ) : (
        <ul className="m-0 flex max-h-[60vh] list-none flex-col gap-1.5 overflow-y-auto p-0">
          {list.map((d) => (
            <li key={d.id} className="rounded-xl border border-[var(--line)] px-3 py-2">
              <div className="flex items-center gap-2">
                <span className={`hookstate ${d.status}`}>{d.status === 'ok' ? `${d.responseStatus ?? 'OK'}` : d.status === 'pending' ? 'offen' : 'Fehler'}</span>
                <span className="min-w-0 flex-1 truncate font-mono text-[12.5px]">{d.event}</span>
                <small className="text-[12px] text-[var(--muted)]">{time(d.createdAt)}</small>
                <button className="iconbtn plain" aria-label="Inhalt anzeigen" data-tip="Inhalt" onClick={() => setOpen(open === d.id ? null : d.id)}>
                  <Icon name={open === d.id ? 'down' : 'chev'} size={16} />
                </button>
              </div>
              {(d.error || d.status === 'pending') && (
                <small className="mt-1 block text-[12.5px] text-[var(--muted)]">
                  {d.error}
                  {d.status === 'pending' && d.nextAttemptAt ? ` · nächster Versuch ${time(d.nextAttemptAt)}` : ''}
                  {d.attempts > 1 ? ` · ${d.attempts} Versuche` : ''}
                </small>
              )}
              {d.status !== 'ok' && (
                <button className="chip mt-1.5" onClick={() => void retry(d)}>Jetzt erneut senden</button>
              )}
              {open === d.id && <pre className="hookpayload">{JSON.stringify(d.payload, null, 2)}</pre>}
            </li>
          ))}
        </ul>
      )}
    </Dialog>
  );
}
